import { Box, Stack, Theme, useMediaQuery } from '@mui/material';

import { LockProvider } from '@work4all/components/lib/hooks';

import {
  FilePreviewPanel,
  PREVIEW_DATA_PROVIDERS,
} from '../../../containers/files/detail/components/file-preview-panel/FilePreviewPanel';
import { useOpenMask } from '../../../containers/mask-overlays/mask-overlay/hooks/use-open-mask';
import { PreviewMobileWrapper } from '../PreviewMobileWrapper';
import { useDeleteEntitiesToolbarConfig } from '../use-delete-entities-toolbar-config';
import { useEntityHandlers } from '../use-entity-handlers';

import { EntityActionProvider } from './EntityActionProvider';
import { EntityPreviewDataProviderProps } from './types';
import { mergeActions } from './utils';

/**
 * Wrapper to handle entity previews.
 */
const EntityPreviewInternal = (props: EntityPreviewDataProviderProps) => {
  const {
    entityType,
    onCloseClick,
    onVisibilityToggle,
    entries,
    custom,
    overrides,
    isPartnerPage,
  } = props;

  const entityIds = entries.map((e) => e.id);
  const onOpenMask = useOpenMask({ entityType });
  const entityHandlers = useEntityHandlers({
    entity: entityType,
    onOpenMask,
    entries,
  });

  const isDesktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up('xl'));
  const deleteConfig = useDeleteEntitiesToolbarConfig({
    entityType,
    selectedEntities: entries,
  });

  return (
    <LockProvider entity={entityType} entityIds={entityIds}>
      {/* // TODO: WW-6794 EntityActionProvider should be removed */}
      <EntityActionProvider entity={entityType}>
        {(actions) => (
          <PreviewMobileWrapper
            active={!isDesktop}
            actions={{
              edit: entityHandlers.edit,
              remove: deleteConfig,
              custom: mergeActions(custom, actions.custom),
            }}
            entityIds={entityIds}
            {...overrides}
          >
            <Stack direction="row" height="100%">
              <Box width="100%">
                <FilePreviewPanel
                  onCloseClick={onCloseClick}
                  onVisibilityToggle={onVisibilityToggle}
                  onEditClicked={entityHandlers.edit?.handler}
                  onShareClicked={entityHandlers.share}
                  openMaskTab={entityHandlers.openMaskTab}
                  convertProps={
                    entityHandlers.convert
                      ? {
                          exclude: entityType,
                          onClick: entityHandlers.convert,
                        }
                      : undefined
                  }
                  customActions={
                    isDesktop ? mergeActions(custom, actions.custom) : undefined
                  }
                  entityIds={entityIds}
                  entityType={entityType}
                  isPartnerPage={isPartnerPage}
                  entries={entries}
                />
              </Box>
            </Stack>
          </PreviewMobileWrapper>
        )}
      </EntityActionProvider>
    </LockProvider>
  );
};

export const EntityPreview = (props: EntityPreviewDataProviderProps) => {
  const PreviewDataProvider = PREVIEW_DATA_PROVIDERS[props.entityType];
  if (PreviewDataProvider) {
    return (
      <PreviewDataProvider {...props}>
        {(dataProps) => {
          return <EntityPreviewInternal {...dataProps} />;
        }}
      </PreviewDataProvider>
    );
  }

  return <EntityPreviewInternal {...props} />;
};
